import { useState } from 'react';
import { DatePicker, InputNumber, Space } from 'antd';
import dayjs from 'dayjs';
import { useNotice } from '../NoticeStack';
import { FilterPanel } from './FilterPanel';

const ISO_DATE = 'YYYY-MM-DD';
const DISPLAY_DATE = 'DD.MM.YYYY';

export interface RangeValue<T extends number | string> {
  from: T | null;
  to: T | null;
}

interface RangePanelBase {
  title: string;
  onDone: () => void;
}

interface NumberRangePanelProps extends RangePanelBase {
  kind: 'number';
  value: RangeValue<number>;
  onChange: (v: RangeValue<number>) => void;
}

interface DateRangePanelProps extends RangePanelBase {
  kind: 'date';
  /** Даты в виде ISO `YYYY-MM-DD`: так они лежат в адресе. */
  value: RangeValue<string>;
  onChange: (v: RangeValue<string>) => void;
}

type RangePanelProps = NumberRangePanelProps | DateRangePanelProps;

const formatThousands = (v: string | number | undefined) =>
  String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const parseThousands = (v: string | undefined) => (v ?? '').replace(/\s/g, '');

function isReversed<T extends number | string>({ from, to }: RangeValue<T>): boolean {
  return from !== null && to !== null && from > to;
}

/**
 * Фильтр-диапазон «с — по» для числа (с разрядами) или даты. Черновик уходит
 * наружу по «Применить»; если «с» больше «по», поля краснеют и показывается
 * окно ошибки, фильтр не меняется. Подсветка гаснет при правке.
 */
export function RangePanel(props: RangePanelProps) {
  const { title, onDone } = props;
  const notice = useNotice();
  const [draft, setDraft] = useState<RangeValue<number | string>>(props.value);
  const [invalid, setInvalid] = useState(false);

  const edit = (patch: Partial<RangeValue<number | string>>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setInvalid(false);
  };

  const apply = () => {
    if (isReversed(draft)) {
      setInvalid(true);
      notice.error('Диапазон задан неверно', [`В фильтре «${title}» значение «с» больше значения «по»`]);
      return;
    }
    if (props.kind === 'number') {
      props.onChange({ from: asNumber(draft.from), to: asNumber(draft.to) });
    } else {
      props.onChange({ from: asString(draft.from), to: asString(draft.to) });
    }
    onDone();
  };

  const reset = () => {
    props.onChange({ from: null, to: null });
    onDone();
  };

  const status = invalid ? 'error' : undefined;

  return (
    <FilterPanel title={title} onApply={apply} onReset={reset}>
      <Space.Compact block>
        {props.kind === 'number' ? (
          <>
            <InputNumber
              aria-label={`${title}: с`}
              placeholder="с"
              status={status}
              value={asNumber(draft.from)}
              onChange={(v) => edit({ from: v })}
              min={0}
              precision={0}
              controls={false}
              formatter={formatThousands}
              parser={parseThousands}
            />
            <InputNumber
              aria-label={`${title}: по`}
              placeholder="по"
              status={status}
              value={asNumber(draft.to)}
              onChange={(v) => edit({ to: v })}
              min={0}
              precision={0}
              controls={false}
              formatter={formatThousands}
              parser={parseThousands}
            />
          </>
        ) : (
          <>
            <DatePicker
              aria-label={`${title}: с`}
              placeholder="с"
              status={status}
              format={DISPLAY_DATE}
              value={toDayjs(draft.from)}
              onChange={(d) => edit({ from: d ? d.format(ISO_DATE) : null })}
            />
            <DatePicker
              aria-label={`${title}: по`}
              placeholder="по"
              status={status}
              format={DISPLAY_DATE}
              value={toDayjs(draft.to)}
              onChange={(d) => edit({ to: d ? d.format(ISO_DATE) : null })}
            />
          </>
        )}
      </Space.Compact>
    </FilterPanel>
  );
}

function asNumber(v: number | string | null): number | null {
  return typeof v === 'number' ? v : null;
}

function asString(v: number | string | null): string | null {
  return typeof v === 'string' ? v : null;
}

function toDayjs(v: number | string | null) {
  return typeof v === 'string' ? dayjs(v, ISO_DATE) : null;
}
